/**
 * The cards Claude waits on: a tool to allow, questions to answer, a plan to approve. The same
 * card shows in the Work view and in the room; once decided it says what was decided and by whom.
 */
import type { PromptAnswer, PromptDecision, PromptSpec } from "@agora/agent-stream";
import { useState } from "react";
import { DiffView, proposedHunks } from "./code";
import { Markdown } from "./markdown";

const DECIDED: Record<PromptDecision, string> = {
  allow: "Allowed once",
  always: "Allowed, always for this project",
  deny: "Not allowed",
  answered: "Answered",
  approved: "Plan approved",
  changes: "Sent back for changes",
  expired: "Expired",
};

export interface PromptCardProps {
  prompt: PromptSpec;
  status: "open" | PromptDecision;
  by?: string;
  note?: string;
  /** What was answered to the questions. */
  answers?: Record<string, string>;
  /** The owner may answer; others see the card read only. */
  canAnswer: boolean;
  onAnswer: (answer: PromptAnswer) => Promise<void>;
  /** In a room the card is compact and the details fold. */
  compact?: boolean;
}

export function PromptCard(p: PromptCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = p.status === "open";
  const answer = async (a: PromptAnswer) => {
    setBusy(true);
    setError(null);
    try {
      await p.onAnswer(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The answer did not go through.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={`wv-scope wv-card wv-card-${p.prompt.kind} ${open ? "is-open" : "is-done"}`} aria-label={p.prompt.title}>
      <header>
        <strong>{p.prompt.title}</strong>
        {p.prompt.reason && <small className="wv-muted">{p.prompt.reason}</small>}
      </header>
      {p.prompt.kind === "permission" && <PermissionBody prompt={p.prompt} compact={p.compact} />}
      {p.prompt.kind === "question" && (open ? <Questions prompt={p.prompt} open={p.canAnswer} busy={busy} onAnswer={answer} /> : <Answered prompt={p.prompt} answers={p.answers} />)}
      {p.prompt.kind === "plan" && <PlanBody prompt={p.prompt} open={open && p.canAnswer} busy={busy} onAnswer={answer} compact={p.compact} />}
      {p.prompt.kind === "permission" && open && p.canAnswer && <PermissionButtons prompt={p.prompt} busy={busy} onAnswer={answer} />}
      {!open && (
        <p className="wv-decided">
          {DECIDED[p.status as PromptDecision]}
          {p.by ? ` by ${p.by}` : ""}
          {p.note ? `: ${p.note}` : ""}
        </p>
      )}
      {open && !p.canAnswer && <p className="wv-muted">Waiting for the workspace owner.</p>}
      {error && (
        <p className="wv-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function Answered({ prompt, answers }: { prompt: PromptSpec; answers?: Record<string, string> }) {
  const qs = prompt.questions ?? [];
  return (
    <ul className="wv-answers">
      {qs.map((q) => (
        <li key={q.question}>
          {q.question} <b>{answers?.[q.question] ?? "(no answer)"}</b>
        </li>
      ))}
    </ul>
  );
}

function PermissionBody({ prompt, compact }: { prompt: PromptSpec; compact?: boolean }) {
  const i = prompt.input;
  const str = (k: string) => (typeof i[k] === "string" ? (i[k] as string) : "");
  let body;
  if (prompt.tool === "Bash") body = <pre className="wv-cmd">{str("command")}</pre>;
  else if ((prompt.tool === "Edit" || prompt.tool === "MultiEdit") && str("file_path")) body = <DiffView path={str("file_path")} hunks={proposedHunks(str("old_string"), str("new_string"))} />;
  else if (prompt.tool === "Write" && str("file_path")) body = <DiffView path={str("file_path")} hunks={[]} created content={str("content")} />;
  else if (prompt.tool === "WebFetch") body = <p className="wv-cmd">{str("url")}</p>;
  else body = Object.keys(i).length ? <pre className="wv-cmd">{JSON.stringify(i, null, 2)}</pre> : null;
  if (!body) return null;
  return compact ? (
    <details className="wv-card-details">
      <summary>Details</summary>
      {body}
    </details>
  ) : (
    body
  );
}

function PermissionButtons({ prompt, busy, onAnswer }: { prompt: PromptSpec; busy: boolean; onAnswer: (a: PromptAnswer) => Promise<void> }) {
  const [note, setNote] = useState("");
  const [saying, setSaying] = useState(false);
  return (
    <div className="wv-card-actions">
      <button type="button" className="wv-primary" disabled={busy} onClick={() => void onAnswer({ kind: "allow" })}>
        Allow once
      </button>
      {prompt.canAlways && (
        <button type="button" disabled={busy} onClick={() => void onAnswer({ kind: "allow", always: true })} title={prompt.always?.join(", ")}>
          Always for this project
        </button>
      )}
      {saying ? (
        <span className="wv-deny-note">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What to do instead (optional)" aria-label="What to do instead" autoFocus />
          <button type="button" disabled={busy} onClick={() => void onAnswer({ kind: "deny", note: note.trim() || undefined })}>
            Send no
          </button>
        </span>
      ) : (
        <button type="button" className="wv-quiet" disabled={busy} onClick={() => setSaying(true)}>
          Deny
        </button>
      )}
    </div>
  );
}

function Questions({ prompt, open, busy, onAnswer }: { prompt: PromptSpec; open: boolean; busy: boolean; onAnswer: (a: PromptAnswer) => Promise<void> }) {
  const qs = prompt.questions ?? [];
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [other, setOther] = useState<Record<string, string>>({});
  const value = (q: string) => (other[q]?.trim() ? other[q]!.trim() : (picked[q] ?? []).join(", "));
  const ready = qs.every((q) => value(q.question));
  return (
    <div className="wv-questions">
      {qs.map((q, qi) => (
        <fieldset key={qi} disabled={!open || busy}>
          <legend>
            <span className="wv-chip">{q.header}</span> {q.question}
          </legend>
          {q.options.map((o) => {
            const on = (picked[q.question] ?? []).includes(o.label);
            return (
              <label key={o.label} className={`wv-option ${on ? "on" : ""}`}>
                <input
                  type={q.multiSelect ? "checkbox" : "radio"}
                  name={`q${qi}-${prompt.promptId}`}
                  checked={on}
                  onChange={() => {
                    setOther({ ...other, [q.question]: "" });
                    setPicked({ ...picked, [q.question]: q.multiSelect ? (on ? (picked[q.question] ?? []).filter((x) => x !== o.label) : [...(picked[q.question] ?? []), o.label]) : [o.label] });
                  }}
                />
                <span>
                  <b>{o.label}</b>
                  {o.description && <small>{o.description}</small>}
                </span>
              </label>
            );
          })}
          <label className="wv-option wv-other">
            <span>Other</span>
            <input value={other[q.question] ?? ""} onChange={(e) => setOther({ ...other, [q.question]: e.target.value })} placeholder="Your own answer" aria-label={`Your own answer to: ${q.question}`} />
          </label>
        </fieldset>
      ))}
      {open && (
        <div className="wv-card-actions">
          <button type="button" className="wv-primary" disabled={busy || !ready} onClick={() => void onAnswer({ kind: "answers", answers: Object.fromEntries(qs.map((q) => [q.question, value(q.question)])) })}>
            Send {qs.length === 1 ? "answer" : "answers"}
          </button>
        </div>
      )}
    </div>
  );
}

function PlanBody({ prompt, open, busy, onAnswer, compact }: { prompt: PromptSpec; open: boolean; busy: boolean; onAnswer: (a: PromptAnswer) => Promise<void>; compact?: boolean }) {
  const [note, setNote] = useState("");
  const plan = <Markdown text={prompt.plan ?? ""} className="wv-plan-text" />;
  return (
    <div className="wv-plan">
      {compact ? (
        <details className="wv-card-details" open={open}>
          <summary>The plan</summary>
          {plan}
        </details>
      ) : (
        plan
      )}
      {open && (
        <div className="wv-card-actions">
          <button type="button" className="wv-primary" disabled={busy} onClick={() => void onAnswer({ kind: "plan", approve: true })}>
            Approve and start
          </button>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What to change in the plan" aria-label="What to change in the plan" />
          <button type="button" disabled={busy || !note.trim()} onClick={() => void onAnswer({ kind: "plan", approve: false, note: note.trim() })}>
            Ask for changes
          </button>
        </div>
      )}
    </div>
  );
}
